import {MinusIcon, PlusIcon} from '@heroicons/react/outline';
import type {ReactNode} from 'react';

import {Button} from '@/components';

/**
 * The presentational pieces the three Settings tables share for their
 * expanded row: a two-column panel of labelled sections, so ops can read
 * everything a country or currency is configured with before deciding to open
 * the Manage page and change it.
 *
 * Shared rather than duplicated per table because the layout is the point:
 * three tables that drift apart visually are three tables ops has to learn
 * separately. Everything here is display-only — editing still happens on the
 * Manage page, which is why nothing in this file takes an onChange.
 */

/** The +/- affordance in the first cell of an expandable row. */
export const ExpandButton = ({
  isExpanded,
  onToggle,
  label,
}: {
  isExpanded: boolean;
  onToggle: () => void;
  /** Names the row for screen readers, e.g. "United States". */
  label: string;
}) => (
  <Button
    onClick={onToggle}
    variant="unStyled"
    aria-expanded={isExpanded}
    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${label}`}
  >
    <div
      className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
        isExpanded ? 'bg-cyan-50' : ''
      }`}
    >
      {isExpanded ? (
        <MinusIcon className="w-4 h-4" />
      ) : (
        <PlusIcon className="w-4 h-4" />
      )}
    </div>
  </Button>
);

/**
 * The panel itself: a card that fills the row, holding two columns.
 *
 * `whitespace-normal` is load-bearing, not tidying. Every panel renders inside
 * a `<Td>`, and `Td` sets `whitespace-nowrap` so the FLAT rows keep each value
 * on one line. `white-space` inherits, so without this reset every paragraph in
 * every panel is forbidden from wrapping and runs off the right edge of the
 * card, which is what the explanatory notes under the permission pills were
 * doing. The nowrap is right for a one-line cell and wrong for a card of prose,
 * so the exception belongs here rather than on each paragraph.
 *
 * `break-words` covers the other half: a value with no spaces to break at (a
 * long reference, an account number) would otherwise push the card wide instead
 * of wrapping.
 */
export const ExpandedPanel = ({children}: {children: ReactNode}) => (
  <div className="flex py-6 px-4 sm:px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
    <div className="bg-white dark:bg-gray-900 rounded-lg w-full px-5 sm:px-8 py-5 sm:py-6 whitespace-normal break-words">
      {children}
    </div>
  </div>
);

/**
 * Two columns on a wide screen, stacked below `md`. The admin is used on
 * laptops, but the tables already scroll horizontally inside their wrapper and
 * a two-column grid that cannot stack would push the panel wider than the row.
 */
export const PanelColumns = ({children}: {children: ReactNode}) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
    {children}
  </div>
);

export const PanelSection = ({
  title,
  children,
  className = '',
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) => (
  <div className={className}>
    <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400 dark:text-slate-300 mb-2">
      {title}
    </h4>
    {children}
  </div>
);

/**
 * One label/value line. `value` is a node, not a string, so a section can put
 * a pill or a "Not set" placeholder in the same column as plain text.
 */
export const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value: ReactNode;
}) => (
  <div className="flex justify-between items-baseline gap-3 py-1.5 border-b last:border-0 border-gray-100 dark:border-dark-borderMid">
    <span className="text-sm text-slate-500 dark:text-slate-300">{label}</span>
    <span className="text-sm font-medium text-gray-700 dark:text-gray-100 text-right break-words">
      {value}
    </span>
  </div>
);

/**
 * The placeholder for a value the record does not carry.
 *
 * Its own component because "not set" is a real, meaningful state across all
 * three tables — a country with no KYC processor falls through to the global
 * default, a currency with no limit falls through to the global default — and
 * rendering it as an empty cell would read as missing data instead.
 */
export const NotSet = ({children = 'Not set'}: {children?: string}) => (
  <span className="text-slate-400 dark:text-slate-500 font-normal">
    {children}
  </span>
);

/**
 * A permission or corridor state, rendered as a pill.
 *
 * `allowed === undefined` is deliberately shown as allowed-but-unset rather
 * than as blocked: that is what the server does (only an explicit `false`
 * blocks), and an ops engineer closing a corridor under pressure should not
 * have to invert it in their head.
 */
const PILL_TONES = {
  blocked:
    'bg-red-50 text-red-600 line-through dark:bg-red-900/30 dark:text-red-300',
  unset: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300',
  allowed:
    'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

const pillTone = (allowed?: boolean): string => {
  if (allowed === false) {
    return PILL_TONES.blocked;
  }
  if (allowed === undefined) {
    return PILL_TONES.unset;
  }
  return PILL_TONES.allowed;
};

export const StatePill = ({
  label,
  allowed,
}: {
  label: string;
  allowed?: boolean;
}) => {
  const isUnset = allowed === undefined;
  const tone = pillTone(allowed);

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${tone}`}
      title={
        isUnset
          ? `${label}: not configured, which counts as allowed`
          : undefined
      }
    >
      {label}
    </span>
  );
};

export const PillRow = ({children}: {children: ReactNode}) => (
  <div className="flex flex-wrap gap-2">{children}</div>
);

/** Bottom-right action area of the panel, where "Manage ..." lives. */
export const PanelActions = ({children}: {children: ReactNode}) => (
  <div className="flex justify-end mt-6">{children}</div>
);
